import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BadgeCheck,
  CalendarDays,
  CircleAlert,
  Eye,
  IndianRupee,
  Target,
  TrendingUp,
  Truck,
  UserCheck,
  Users,
  WalletCards,
  XCircle,
} from "lucide-react";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

if (!Tooltip.positioners.cursor) {
  Tooltip.positioners.cursor = function (_elements, eventPosition) {
    const chartArea = this?.chart?.chartArea;

    if (!chartArea) {
      return {
        x: eventPosition.x,
        y: eventPosition.y,
      };
    }

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    return {
      x: eventPosition.x,
      y: eventPosition.y,
      xAlign: eventPosition.x > centerX ? "right" : "left",
      yAlign: eventPosition.y > centerY ? "bottom" : "top",
    };
  };
}

const clamp = (value, min = 0, max = 100) =>
  Math.min(max, Math.max(min, Number(value || 0)));

const formatSigned = (value, suffix = "") => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return `0${suffix}`;
  if (number > 0) return `+${number.toFixed(1)}${suffix}`;
  return `${number.toFixed(1)}${suffix}`;
};

const SectionInfo = ({ title, description, dark = false, className = "" }) => {
  return (
    <div className={`group relative shrink-0 ${className}`}>
      <button
        type="button"
        aria-label={`About ${title}`}
        className={`flex h-7 w-7 items-center justify-center rounded-md border transition ${
          dark
            ? "border-white/20 bg-white/10 text-white hover:bg-white/20"
            : "border-gray-200 bg-white text-gray-500 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-600"
        }`}
      >
        <Eye size={13} />
      </button>

      <div className="pointer-events-none invisible absolute right-0 top-full z-[150] mt-1.5 w-[290px] max-w-[calc(100vw-24px)] translate-y-1 rounded-lg border border-gray-200 bg-white p-2.5 text-left opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
        <p className="text-xs font-black text-gray-900">{title}</p>
        <p className="mt-1 text-[10px] leading-4 text-gray-600">
          {description}
        </p>
      </div>
    </div>
  );
};

const KpiCard = ({
  label,
  value,
  helper,
  Icon,
  tone = "slate",
  badge,
  info,
}) => {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-900",
    orange: "border-orange-100 bg-orange-50/70 text-orange-900",
    green: "border-emerald-100 bg-emerald-50/70 text-emerald-900",
    red: "border-red-100 bg-red-50/70 text-red-900",
    blue: "border-blue-100 bg-blue-50/70 text-blue-900",
    purple: "border-purple-100 bg-purple-50/70 text-purple-900",
  };

  return (
    <div className={`rounded-xl border p-3 shadow-sm ${tones[tone]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
              {label}
            </p>
            {badge && (
              <span className="rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-bold shadow-sm">
                {badge}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xl font-black tracking-tight">
            {value}
          </p>
          <p className="mt-0.5 text-[10px] leading-4 opacity-65">{helper}</p>
        </div>
        <div className="flex shrink-0 items-start gap-1.5">
          {info && <SectionInfo title={label} description={info} />}
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm">
            <Icon className="h-4 w-4 opacity-80" />
          </div>
        </div>
      </div>
    </div>
  );
};

const DeltaPill = ({ value, suffix = "", inverse = false }) => {
  const numeric = Number(value || 0);
  const positive = inverse ? numeric < 0 : numeric > 0;
  const negative = inverse ? numeric > 0 : numeric < 0;
  const Icon = numeric >= 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${
        positive
          ? "bg-emerald-50 text-emerald-700"
          : negative
            ? "bg-red-50 text-red-700"
            : "bg-gray-100 text-gray-600"
      }`}
    >
      <Icon size={11} />
      {formatSigned(numeric, suffix)}
    </span>
  );
};

const DriverIntelligenceCharts = ({
  totals,
  activeDrivers,
  overallSuccessRate,
  overallDeclineRate,
  avgTasksPerActiveDriver,
  amountPerSuccessfulTask,
  driverMappingRate,
  mappedDrivers,
  driverSummary,
  weekSummary,
  top3TaskShare,
  attentionDrivers,
  topVolumeDriver,
  bestSuccessDriver,
  highestDeclineDriver,
  inactiveDrivers,
  latestWeek,
  weekOverWeek,
  zeroAmountSuccessRows,
  weekBucketsOutsideFilter,
  fromDate,
  toDate,
  assignmentActivityData,
  weeklyTrendData,
  driverChartHeight,
  allDriverOutcomeData,
  getDriverFromOutcomeLabel,
  getVisibleOutcomeDriverName,
  successRateData,
  performanceBandData,
  amountMixData,
  formatCurrency,
}) => {
  return (
    <>
      {/* Executive KPI strip */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
        <KpiCard
          label="Assigned"
          value={totals.assigned.toLocaleString("en-IN")}
          helper={`${activeDrivers.length} active report drivers`}
          Icon={Truck}
          tone="orange"
          info="Total assigned tasks summed across every driver-week row returned by the API. This is the workload reference used for overall task rates."
        />
        <KpiCard
          label="Successful"
          value={totals.successful.toLocaleString("en-IN")}
          helper={`${overallSuccessRate.toFixed(1)}% weighted success rate`}
          Icon={BadgeCheck}
          tone="green"
          info="Total successful tasks across the returned report. The weighted success rate is calculated as total successful divided by total assigned, so high-volume drivers have proportional impact."
        />
        <KpiCard
          label="Declined"
          value={totals.declined.toLocaleString("en-IN")}
          helper={`${overallDeclineRate.toFixed(1)}% of assigned tasks`}
          Icon={XCircle}
          tone="red"
          info="Total declined tasks across the returned report. Decline rate is total declined divided by total assigned and is useful for identifying operational rejection risk."
        />
        <KpiCard
          label="Pending / Other"
          value={totals.pending.toLocaleString("en-IN")}
          helper="Assigned minus successful and declined"
          Icon={CircleAlert}
          tone="orange"
          info="Derived value, not a direct API field. Pending / Other = max(assigned - successful - declined, 0) for each driver-week row, then summed."
        />
        <KpiCard
          label="Avg Load / Driver"
          value={avgTasksPerActiveDriver.toFixed(1)}
          helper="Assigned tasks per active report driver"
          Icon={Users}
          tone="blue"
          info="Average workload across active report drivers only. It is calculated as total assigned tasks divided by drivers whose assigned total is greater than zero."
        />
        <KpiCard
          label="Reported Amount"
          value={formatCurrency(totals.reportedAmount)}
          helper="Week + per-order + extra fields"
          Icon={WalletCards}
          tone="purple"
          info="Sum of the three amount fields returned by the API: total_week_amount + total_per_order_amount + total_extra_amount. It is treated as reported amount and is not automatically assumed to be paid/settled."
        />
        <KpiCard
          label="Amount / Success"
          value={formatCurrency(amountPerSuccessfulTask)}
          helper="Reported amount divided by successful tasks"
          Icon={IndianRupee}
          tone="purple"
          info="Reported amount divided by successful tasks. This is a derived efficiency ratio for comparison; it should not be interpreted as a contractual per-order payout unless your backend defines it that way."
        />
        <KpiCard
          label="Driver Mapping"
          value={`${driverMappingRate.toFixed(0)}%`}
          helper={`${mappedDrivers}/${driverSummary.length || 0} report codes matched to driver list`}
          Icon={UserCheck}
          tone="slate"
          info="Shows how many driver codes returned by the weekly API were matched to the loaded driver master list. A lower percentage means some names/team/contact details cannot be resolved."
        />
      </div>

      {/* Executive narrative + week-over-week */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        <div className="relative overflow-hidden rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 p-4 text-white shadow-sm xl:col-span-2">
          <div className="absolute -right-14 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-white/10" />

          <div className="relative z-10">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                  Executive Summary
                </span>
                <span className="text-xs text-white/80">
                  {weekSummary.length} API week bucket
                  {weekSummary.length === 1 ? "" : "s"}
                </span>
              </div>
              <SectionInfo
                dark
                title="Executive Summary"
                description="High-level admin summary of total workload, weighted success rate, active drivers, top-3 workload concentration and drivers meeting the attention rule. The four mini-cards highlight top volume, best eligible success rate, highest eligible decline rate and drivers with zero assigned tasks."
              />
            </div>

            <h3 className="mt-2 text-xl font-black leading-tight md:text-2xl">
              {overallSuccessRate.toFixed(1)}% success across{" "}
              {totals.assigned.toLocaleString("en-IN")} assigned tasks
            </h3>

            <p className="mt-2 max-w-4xl text-xs leading-5 text-white/90">
              {activeDrivers.length} drivers handled activity in the returned
              report. The top 3 drivers carry {top3TaskShare.toFixed(1)}% of
              assigned volume. {attentionDrivers.length} driver
              {attentionDrivers.length === 1 ? "" : "s"} meet the attention rule
              of at least 5 assigned tasks with success below 75% or decline
              at/above 25%.
            </p>

            <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-wide text-white/70">
                  Top Volume
                </p>
                <p className="mt-1 truncate text-sm font-bold">
                  {topVolumeDriver?.driverName || "-"}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  {topVolumeDriver?.assigned || 0} assigned
                </p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-wide text-white/70">
                  Best Rate*
                </p>
                <p className="mt-1 truncate text-sm font-bold">
                  {bestSuccessDriver?.driverName || "-"}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  {bestSuccessDriver
                    ? `${bestSuccessDriver.successRate.toFixed(1)}%`
                    : "-"}
                </p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-wide text-white/70">
                  Highest Decline*
                </p>
                <p className="mt-1 truncate text-sm font-bold">
                  {highestDeclineDriver?.driverName || "-"}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  {highestDeclineDriver
                    ? `${highestDeclineDriver.declineRate.toFixed(1)}%`
                    : "-"}
                </p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 p-2.5 backdrop-blur-sm">
                <p className="text-[10px] uppercase tracking-wide text-white/70">
                  No Activity
                </p>
                <p className="mt-0.5 text-lg font-black">
                  {inactiveDrivers.length}
                </p>
                <p className="mt-1 text-xs text-white/75">
                  drivers returned with 0 assigned
                </p>
              </div>
            </div>

            <p className="mt-2 text-[9px] text-white/65">
              *Performance comparison uses drivers with at least 5 assigned
              tasks.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-black text-gray-900">
                Latest Week Movement
              </h3>
              <p className="text-[9px] leading-4 text-gray-500">
                {latestWeek
                  ? `${latestWeek.weekStart} → ${latestWeek.weekEnd}`
                  : "No week data"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SectionInfo
                title="Latest Week Movement"
                description="Compares the latest two week buckets returned by the API. Every delta is Latest Week minus Previous Week. For declined tasks, a decrease is treated as positive. This comparison follows returned week buckets, which may not exactly match the selected date boundaries."
              />
              <TrendingUp className="h-5 w-5 text-orange-500" />
            </div>
          </div>

          {!weekOverWeek ? (
            <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-3 text-center text-xs text-gray-500">
              At least two returned week buckets are required for week-over-week
              comparison.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2">
                <span className="text-xs font-semibold text-gray-600">
                  Assigned tasks
                </span>
                <DeltaPill value={weekOverWeek.assignedDelta} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2">
                <span className="text-xs font-semibold text-gray-600">
                  Successful tasks
                </span>
                <DeltaPill value={weekOverWeek.successfulDelta} />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2">
                <span className="text-xs font-semibold text-gray-600">
                  Declined tasks
                </span>
                <DeltaPill value={weekOverWeek.declinedDelta} inverse />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2">
                <span className="text-xs font-semibold text-gray-600">
                  Success rate
                </span>
                <DeltaPill value={weekOverWeek.successRateDelta} suffix=" pp" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-2">
                <span className="text-xs font-semibold text-gray-600">
                  Reported amount
                </span>
                <span
                  className={`text-xs font-black ${weekOverWeek.amountDelta >= 0 ? "text-emerald-700" : "text-red-700"}`}
                >
                  {weekOverWeek.amountDelta >= 0 ? "+" : ""}
                  {formatCurrency(weekOverWeek.amountDelta)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data quality / operational flags */}
      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <div
          className={`rounded-xl border p-3 ${attentionDrivers.length ? "border-red-100 bg-red-50" : "border-emerald-100 bg-emerald-50"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle
                className={`h-4 w-4 ${attentionDrivers.length ? "text-red-600" : "text-emerald-600"}`}
              />
              <p className="text-xs font-black text-gray-900">
                Performance Attention
              </p>
            </div>
            <SectionInfo
              title="Performance Attention"
              description="Counts drivers with at least 5 assigned tasks whose success rate is below 75% OR decline rate is 25% or higher. It is a dashboard review rule derived from the report, not a status returned directly by the API."
            />
          </div>
          <p className="mt-1 text-xl font-black text-gray-900">
            {attentionDrivers.length}
          </p>
          <p className="text-[9px] leading-4 text-gray-600">
            Drivers meeting the defined low-success/high-decline rule.
          </p>
        </div>

        <div
          className={`rounded-xl border p-3 ${zeroAmountSuccessRows.length ? "border-amber-100 bg-amber-50" : "border-emerald-100 bg-emerald-50"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <WalletCards className="h-4 w-4 text-amber-600" />
              <p className="text-xs font-black text-gray-900">
                ₹0 Amount + Success
              </p>
            </div>
            <SectionInfo
              title="₹0 Amount + Success"
              description="Counts driver-week rows where successful_tasks is greater than zero but total_week_amount, total_per_order_amount and total_extra_amount together equal zero. This is a diagnostic flag for admin review, not proof of a payment error."
            />
          </div>
          <p className="mt-1 text-xl font-black text-gray-900">
            {zeroAmountSuccessRows.length}
          </p>
          <p className="text-[9px] leading-4 text-gray-600">
            Driver-week rows with successful tasks but all reported amount
            fields equal to zero.
          </p>
        </div>

        <div
          className={`rounded-xl border p-3 ${weekBucketsOutsideFilter.length ? "border-amber-100 bg-amber-50" : "border-emerald-100 bg-emerald-50"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-amber-600" />
              <p className="text-xs font-black text-gray-900">
                Range Diagnostics
              </p>
            </div>
            <SectionInfo
              title="Range Diagnostics"
              description="Counts returned week buckets that are fully outside the requested From/To date range. It helps detect when the weekly API returns full calendar-week buckets beyond the exact filter dates."
            />
          </div>
          <p className="mt-1 text-xl font-black text-gray-900">
            {weekBucketsOutsideFilter.length}
          </p>
          <p className="text-[9px] leading-4 text-gray-600">
            Returned week buckets fully outside the requested {fromDate} →{" "}
            {toDate} range.
          </p>
        </div>

        <div
          className={`rounded-xl border p-3 ${driverMappingRate < 100 ? "border-blue-100 bg-blue-50" : "border-emerald-100 bg-emerald-50"}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-blue-600" />
              <p className="text-xs font-black text-gray-900">
                Driver Directory Match
              </p>
            </div>
            <SectionInfo
              title="Driver Directory Match"
              description="Matches weekly report driver_code values against driverId from the loaded driver master list. Matched drivers can show full name, team and contact; unmatched codes fall back to the code itself."
            />
          </div>
          <p className="mt-1 text-xl font-black text-gray-900">
            {mappedDrivers}/{driverSummary.length || 0}
          </p>
          <p className="text-[9px] leading-4 text-gray-600">
            Weekly API driver codes matched against the loaded driver master
            list.
          </p>
        </div>
      </div>

      {/* Primary visuals */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm xl:col-span-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-black text-gray-900">
                Assignment & Reassignment Activity
              </h3>
              <p className="text-[9px] leading-4 text-gray-500">
                Driver-level assignment activity; declined attempts are not
                treated as failed orders.
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <SectionInfo
                title="Assignment & Reassignment Activity"
                description="This is not a unique-order outcome chart. Assigned, Successful and Declined are driver-level records returned by the API. A task can be declined by one driver and later completed successfully by another driver, so the same underlying order may contribute to both Declined and Successful counts. Because this API does not return task/order IDs, the dashboard cannot safely deduplicate those reassignments. Pending / Other is derived as max(assigned - successful - declined, 0)."
              />
              <Target className="h-4 w-4 text-orange-500" />
            </div>
          </div>

          <div className="mt-2 h-[205px]">
            <Bar
              data={assignmentActivityData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = Number(context.raw || 0);
                        const label = context.label || "";

                        if (label === "Declined Attempts") {
                          return `${label}: ${value} (${overallDeclineRate.toFixed(1)}% of assignment attempts)`;
                        }

                        if (label === "Successful Handles") {
                          return `${label}: ${value} (${overallSuccessRate.toFixed(1)}% of assignment attempts)`;
                        }

                        return `${label}: ${value}`;
                      },
                    },
                  },
                },
                scales: {
                  x: { grid: { display: false } },
                  y: { beginAtZero: true, ticks: { precision: 0 } },
                },
              }}
            />
          </div>

          <div className="mt-1 rounded-lg border border-amber-100 bg-amber-50 px-2 py-1.5 text-[9px] leading-3.5 text-amber-800">
            <span className="font-black">Decline ≠ failed order.</span> A
            declined assignment may be reassigned and later completed by another
            driver. Unique final-order success needs task/order IDs from the
            API.
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm xl:col-span-8">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">Weekly Task Trend</h3>
              <p className="mt-1 text-xs text-gray-500">
                Assigned, successful and declined totals for every week bucket
                returned by the API.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SectionInfo
                title="Weekly Task Trend"
                description="Aggregates every driver row into each returned week bucket and plots Assigned, Successful and Declined totals over time. Use it to see whether workload and outcomes are improving or deteriorating week by week."
              />
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-bold text-gray-600">
                {weekSummary.length} weeks
              </span>
            </div>
          </div>
          <div className="mt-2 h-[230px]">
            <Line
              data={weeklyTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: { usePointStyle: true, boxWidth: 8 },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { maxRotation: 0 },
                  },
                  y: { beginAtZero: true, ticks: { precision: 0 } },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Driver outcome + rate visuals */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">
                All Driver Workload & Outcome
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Every returned driver; stacked bars show successful, declined
                and pending/other tasks.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SectionInfo
                title="All Driver Workload & Outcome"
                description="Each horizontal row is one driver. The stacked bar is the outcome breakup: Successful + Declined + Pending / Other. Assigned is the API workload reference and is shown in the chart tooltip. Pending / Other is derived as max(assigned - successful - declined, 0). Hover a driver to see exact Assigned, Successful, Declined, Pending, Success Rate and Decline Rate values."
              />
              <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[9px] font-bold text-orange-700">
                {driverSummary.length} drivers
              </span>
            </div>
          </div>

          {driverSummary.length === 0 ? (
            <div className="flex h-72 items-center justify-center text-sm text-gray-400">
              No driver data available
            </div>
          ) : (
            <div className="mt-2 w-full overflow-x-auto overflow-y-visible">
              <div
                style={{
                  height: `${driverChartHeight}px`,
                  minHeight: "310px",
                }}
              >
                <Bar
                  data={allDriverOutcomeData}
                  options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    // IMPORTANT for horizontal bars:
                    // index mode defaults to the X axis in Chart.js. That can
                    // select a different driver's index. Force Y-axis row
                    // detection so the hovered row and tooltip always match.
                    interaction: {
                      mode: "index",
                      axis: "y",
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { usePointStyle: true, boxWidth: 8 },
                      },
                      tooltip: {
                        // Custom positioner keeps the tooltip beside cursor
                        // instead of in the middle of the stacked segments.
                        position: "cursor",
                        callbacks: {
                          title: (items) => {
                            const driver = getDriverFromOutcomeLabel(
                              items?.[0]?.label,
                            );

                            return driver
                              ? `${driver.driverName} (${driver.driverCode})`
                              : getVisibleOutcomeDriverName(
                                  items?.[0]?.label || "",
                                );
                          },
                          label: (context) => {
                            const value = Number(context.raw || 0);
                            return `${context.dataset.label}: ${value}`;
                          },
                          afterBody: (items) => {
                            const driver = getDriverFromOutcomeLabel(
                              items?.[0]?.label,
                            );

                            if (!driver) return [];

                            // Successful / Declined / Pending are already
                            // shown once above by each dataset label.
                            return [
                              "",
                              `Assigned: ${driver.assigned}`,
                              `Success Rate: ${driver.successRate.toFixed(1)}%`,
                              `Decline Rate: ${driver.declineRate.toFixed(1)}%`,
                            ];
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        stacked: true,
                        beginAtZero: true,
                        ticks: { precision: 0 },
                      },
                      y: {
                        stacked: true,
                        grid: { display: false },
                        ticks: {
                          autoSkip: false,
                          callback: function (value) {
                            const rawLabel = this.getLabelForValue(value);
                            const label = getVisibleOutcomeDriverName(rawLabel);

                            return label.length > 22
                              ? `${label.slice(0, 22)}…`
                              : label;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">
                Success Rate by Driver
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                All returned drivers on a fixed 0–100% scale for fast
                comparison.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SectionInfo
                title="Success Rate by Driver"
                description="Shows each driver's success rate on a fixed 0–100% scale. Success Rate = successful tasks / assigned tasks × 100. Bar colors classify performance; drivers with zero assigned tasks are shown neutrally. The weighted total uses all successful tasks divided by all assigned tasks."
              />
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-700">
                Weighted total {overallSuccessRate.toFixed(1)}%
              </span>
            </div>
          </div>

          {driverSummary.length === 0 ? (
            <div className="flex h-72 items-center justify-center text-sm text-gray-400">
              No driver data available
            </div>
          ) : (
            <div className="mt-2 w-full overflow-x-auto overflow-y-visible">
              <div
                style={{
                  height: `${driverChartHeight}px`,
                  minHeight: "310px",
                }}
              >
                <Bar
                  data={successRateData}
                  options={{
                    indexAxis: "y",
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (context) =>
                            `Success rate: ${Number(context.raw || 0).toFixed(1)}%`,
                          afterBody: (items) => {
                            const index = items?.[0]?.dataIndex ?? -1;
                            const driver = driverSummary[index];
                            return driver
                              ? [
                                  `Assigned: ${driver.assigned}`,
                                  `Successful: ${driver.successful}`,
                                  `Declined: ${driver.declined}`,
                                ]
                              : [];
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        ticks: { callback: (value) => `${value}%` },
                      },
                      y: {
                        grid: { display: false },
                        ticks: {
                          autoSkip: false,
                          callback: function (value) {
                            const label = this.getLabelForValue(value);
                            return label.length > 22
                              ? `${label.slice(0, 22)}…`
                              : label;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Secondary BI visuals */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm xl:col-span-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">
                Driver Performance Bands
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Active drivers grouped by success rate.
              </p>
            </div>
            <SectionInfo
              title="Driver Performance Bands"
              description="Groups only active drivers (assigned > 0) by success rate: Excellent = 90%+, Good = 75–89.99%, Watch = 60–74.99%, Critical = below 60%. This quickly shows the distribution of driver performance quality."
            />
          </div>
          <div className="mt-2 h-[205px]">
            <Doughnut
              data={performanceBandData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "62%",
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      usePointStyle: true,
                      boxWidth: 8,
                      padding: 12,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm xl:col-span-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">Reported Amount Mix</h3>
              <p className="mt-1 text-xs text-gray-500">
                Composition of the three amount fields returned by the API.
              </p>
            </div>
            <SectionInfo
              title="Reported Amount Mix"
              description="Breaks the total reported amount into the exact API components: total_week_amount, total_per_order_amount and total_extra_amount. The chart describes composition only; the dashboard does not assume these values are already paid or settled."
            />
          </div>
          <div className="mt-2 h-[205px]">
            <Doughnut
              data={amountMixData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "62%",
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      usePointStyle: true,
                      boxWidth: 8,
                      padding: 12,
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) =>
                        `${context.label}: ${formatCurrency(context.raw)}`,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm xl:col-span-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="font-black text-gray-900">Operational Ratios</h3>
              <p className="mt-1 text-xs text-gray-500">
                Compact ratios useful for admin review.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SectionInfo
                title="Operational Ratios"
                description="Compact admin ratios: overall Success Rate, overall Decline Rate, Top-3 Workload Share (concentration of assigned tasks among the three busiest drivers), and Driver Directory Mapping percentage."
              />
              <Activity className="h-5 w-5 text-orange-500" />
            </div>
          </div>

          <div className="mt-3 space-y-3">
            {[
              {
                label: "Success rate",
                value: overallSuccessRate,
                color: "bg-emerald-500",
              },
              {
                label: "Decline rate",
                value: overallDeclineRate,
                color: "bg-red-500",
              },
              {
                label: "Top 3 workload share",
                value: top3TaskShare,
                color: "bg-orange-500",
              },
              {
                label: "Driver directory mapping",
                value: driverMappingRate,
                color: "bg-blue-500",
              },
            ].map((metric) => (
              <div key={metric.label}>
                <div className="mb-1 flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-gray-600">
                    {metric.label}
                  </span>
                  <span className="font-black text-gray-900">
                    {clamp(metric.value).toFixed(1)}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full rounded-full ${metric.color}`}
                    style={{ width: `${clamp(metric.value)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attention table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-1.5 border-b border-gray-100 p-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-black text-gray-900">
              Drivers Requiring Attention
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Rule: at least 5 assigned tasks and success &lt;75% or decline
              ≥25%.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SectionInfo
              title="Drivers Requiring Attention"
              description="Detailed list of drivers meeting the dashboard attention rule: at least 5 assigned tasks and either success rate below 75% or decline rate at/above 25%. Sort order prioritizes higher decline rate, then lower success rate."
            />
            <span
              className={`w-fit rounded-full px-2.5 py-1 text-[10px] font-black ${attentionDrivers.length ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}
            >
              {attentionDrivers.length} flagged
            </span>
          </div>
        </div>

        {attentionDrivers.length === 0 ? (
          <div className="flex min-h-[110px] items-center justify-center text-sm text-gray-500">
            No drivers meet the attention rule in this report.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[850px] w-full text-[10px]">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-2 py-1.5">Driver</th>
                  <th className="px-2 py-1.5">Team</th>
                  <th className="px-2 py-1.5 text-right">Assigned</th>
                  <th className="px-2 py-1.5 text-right">Successful</th>
                  <th className="px-2 py-1.5 text-right">Declined</th>
                  <th className="px-2 py-1.5 text-right">Success Rate</th>
                  <th className="px-2 py-1.5 text-right">Decline Rate</th>
                  <th className="px-2 py-1.5 text-right">Reported Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {attentionDrivers.map((driver) => (
                  <tr key={driver.driverCode} className="hover:bg-red-50/30">
                    <td className="px-2 py-1.5">
                      <p className="font-bold text-gray-900">
                        {driver.driverName}
                      </p>
                      <p className="text-[9px] text-gray-500">
                        {driver.driverCode}
                      </p>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600">
                      {driver.teamName}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-gray-900">
                      {driver.assigned}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-emerald-700">
                      {driver.successful}
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-red-700">
                      {driver.declined}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <span className="rounded-full bg-orange-50 px-1.5 py-0.5 font-black text-orange-700">
                        {driver.successRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <span className="rounded-full bg-red-50 px-1.5 py-0.5 font-black text-red-700">
                        {driver.declineRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-bold text-gray-900">
                      {formatCurrency(driver.reportedAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default DriverIntelligenceCharts;
